import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Divider,
    Grid2,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    GET_NULL_CLASS,
    GET_NULL_DIVISION,
    STATUS,
    DivisionToInsert,
    ClassToInsert,
    Division,
    DIVISION_LIMITATION,
    DIVISION_STYLE,
    getBirthdayCutOffOptions,
} from '@type/db-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { EventForm } from '@components/eventCreation/CreateEventUtils';
import { AddCircleOutline, DragIndicator, RemoveCircleOutline, Scale } from '@mui/icons-material';
import FormInputSelect from '@components/forms/FormInputSelect';
import FormInputNumber from '@components/forms/FormInputNumber';
import invariant from 'tiny-invariant';
import { reorder } from '@utils/drag-utils';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import useWatchWeightTolerance from '@hooks/useWatchWeightTolerance';
import DivisionLimitForm from './DivisionLimitForm';
import BracketStyleForm from './BracketStyleForm';

const getDraggableIdFromIndex = (index: number): string => `division-${index}`;

const getIndexFromDraggableId = (draggableId: string): number =>
    Number(draggableId.replace('division-', ''));

export default function DivisionEditor() {
    const { control, setValue, trigger, watch } = useFormContext<EventForm>();
    const [reSortDivisions, setReSortDivisions] = useState(false);

    const [divisions, startAt] = watch(['divisions', 'startAt']);

    const { /* fields, */ append, move, remove, update } = useFieldArray({
        control,
        name: 'divisions',
    });

    // ask to auto adjust weight tolerance when changing it
    const adjustWeightsConfirm = useWatchWeightTolerance();

    const birthdayCutoffOptions = useMemo(() => getBirthdayCutOffOptions(startAt), [startAt]);

    useEffect(() => {
        // update division sort indexes when the divisions change
        if (reSortDivisions) {
            const updated = divisions.map((division, i) => ({ ...division, sortIndex: i + 1 }));
            setReSortDivisions(false);
            setValue('divisions', updated);
        }
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reSortDivisions]);

    const onDragEnd = useCallback(
        (result: DropResult): void => {
            // dropped outside the list
            if (!result.destination) {
                return;
            }

            if (result.type === 'division') {
                move(result.source.index, result.destination.index);
                setReSortDivisions(true);

                return;
            }

            if (result.type === 'weight-class') {
                const divisionParent =
                    divisions[getIndexFromDraggableId(result.source.droppableId)];

                invariant(divisionParent, 'could not find nested list');

                const updated: DivisionToInsert | Division = {
                    ...divisionParent,
                    classes: reorder(
                        divisionParent?.classes,
                        result.source.index,
                        result.destination.index,
                    ).map((c, i) => ({ ...c, sortIndex: i + 1 })),
                };

                const divisionIndex = divisions.indexOf(divisionParent as Division);

                update(divisionIndex, updated);
            }
        },
        [divisions, move, update],
    );

    const recentBracketStyle = divisions?.[divisions.length - 1]?.bracketStyle;

    return (
        <>
            <Grid2 container alignItems={'center'} spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                    <Alert severity="info">
                        Divisions are groups of wrestlers that will compete against each other. Each
                        division can have multiple weight classes. Or you can use the Madison System
                        and group wrestlers into brackets later.
                    </Alert>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormInputSelect
                        name="birthdayCutoff"
                        label="Wrestler age as of"
                        options={birthdayCutoffOptions}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormInputNumber
                        label="Weight Tolerance:"
                        name="weightTolerance"
                        numberInputProps={{ placeholder: '0' }}
                        tooltip={`The maximum weight difference allowed wrestler weight in and the max weight for the class. For example, a weight tolerance of 2 lbs would allow a 152 lb wrestler to compete in the 150 lb weight class. Max Weight = 150 lbs + 2 lbs = 152 lbs. We will adjust the max weights for the classes accordingly.`}
                        units={'lbs'}
                    />
                </Grid2>
                {recentBracketStyle ? null : (
                    <Grid2>
                        <BracketStyleForm
                            onChange={(bracketStyle: DIVISION_STYLE) => {
                                if (divisions[0]) {
                                    update(0, { ...divisions[0], bracketStyle });
                                } else {
                                    append({ ...GET_NULL_DIVISION(0, bracketStyle) });
                                }
                                trigger('divisions');
                            }}
                            value={divisions[0]?.bracketStyle}
                            sx={{ marginBottom: 4, marginTop: 2 }}
                        />
                    </Grid2>
                )}
            </Grid2>
            {recentBracketStyle ? (
                <>
                    <Divider sx={{ marginY: 1 }} />
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Controller
                            name="divisions"
                            control={control}
                            render={({ field: { value: divisions /* name */ } }) => (
                                <Droppable
                                    droppableId={'division'}
                                    type={'division'}
                                    key={'division'}
                                >
                                    {(dropProvided, dropSnapshot) => (
                                        <Box
                                            ref={dropProvided.innerRef}
                                            paddingBottom="1rem"
                                            sx={{
                                                backgroundColor: dropSnapshot.isDraggingOver
                                                    ? 'rgba(0, 168, 34, .3)'
                                                    : 'white',
                                                transition: 'background-color 0.1s ease',
                                                // Focus styles
                                                ':focus': {
                                                    outline: '2px solid blue',
                                                    outlineOffset: '2px',
                                                },
                                            }}
                                            {...dropProvided.droppableProps}
                                        >
                                            {divisions
                                                ?.sort((a, b) => a.sortIndex - b.sortIndex)
                                                .map(
                                                    (
                                                        division: Division | DivisionToInsert,
                                                        index,
                                                    ) => (
                                                        <DivisionInfoEditor
                                                            division={division}
                                                            allowRemove={divisions.length > 1}
                                                            key={`division-${('id' in division && division.id) || index}`}
                                                            index={index}
                                                            remove_unsafe={() => remove(index)}
                                                            onChange={(updatedDivision) =>
                                                                update(index, updatedDivision)
                                                            }
                                                        />
                                                    ),
                                                )}

                                            {/* Placeholder for dragging */}
                                            {dropProvided.placeholder}

                                            <Stack direction={'row'} spacing={2}>
                                                <Button
                                                    startIcon={<AddCircleOutline />}
                                                    variant="outlined"
                                                    onClick={() => {
                                                        console.log(
                                                            `addDivision ${divisions.length}`,
                                                        );
                                                        append(
                                                            GET_NULL_DIVISION(
                                                                divisions.length,
                                                                recentBracketStyle,
                                                            ),
                                                        );
                                                        trigger('divisions');
                                                    }}
                                                >
                                                    Add division
                                                </Button>
                                            </Stack>
                                        </Box>
                                    )}
                                </Droppable>
                            )}
                        />
                    </DragDropContext>
                    {adjustWeightsConfirm}
                </>
            ) : null}
        </>
    );
}

interface DivisionInfoEditorProps {
    division: DivisionToInsert;
    // field: FieldArrayWithId<EventForm, 'divisions', 'id'>;
    index: number;
    remove_unsafe: () => void;
    allowRemove: boolean;
    onChange: (division: DivisionToInsert) => void;
}

/* TODO: Add collapsing the division and all its classes */
function DivisionInfoEditor({
    // field,
    division,
    index,
    remove_unsafe,
    allowRemove,
    onChange,
}: DivisionInfoEditorProps) {
    const { name, bracketStyle, classes, entryLimitation } = division;
    const { trigger } = useFormContext<EventForm>();
    const isRemoved = division.status !== STATUS.ACTIVE;

    if (isRemoved) return null;

    const limitationSelected = entryLimitation && entryLimitation !== 'none';

    const removeButton = allowRemove ? (
        <IconButton
            color="error"
            type="reset"
            onClick={() => {
                if ('id' in division && division.id) {
                    // if the division already exists in our DB, we need to mark it as deleted, not remove it from the form
                    Object.assign(division, { status: STATUS.REMOVED });
                } else {
                    remove_unsafe();
                }
                console.log('removal complete');
            }}
        >
            <RemoveCircleOutline />
        </IconButton>
    ) : null;

    return (
        <Draggable
            draggableId={getDraggableIdFromIndex(index)}
            key={getDraggableIdFromIndex(index)}
            index={index}
        >
            {(dragProvided) => (
                <Box
                    ref={dragProvided.innerRef}
                    sx={{
                        background: 'linear-gradient(to bottom, #f9f9f9, transparent)',
                        borderRadius: 2,
                        padding: 0.5,
                    }}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                >
                    <Grid2 container alignItems={'center'} spacing={2} paddingY={1}>
                        <Grid2 size={{ xs: 12, sm: 5 }}>
                            <Box
                                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                            >
                                <DragIndicator sx={{ paddingRight: 0.25 }} />
                                <TextField
                                    autoFocus={index === 0 && !name} // focus the first division name input if it's empty
                                    fullWidth
                                    label={`Division Name #${index + 1}`}
                                    key={`division-name-${index}`}
                                    // helperText={error ? error.message : null}
                                    // error={!!error}
                                    onChange={({ target: { value } }) => {
                                        onChange({ ...division, name: value });
                                        trigger('divisions');
                                    }}
                                    required
                                    size="small"
                                    value={name}
                                    variant="outlined"
                                />
                            </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 11, sm: 6 }}>
                            <BracketStyleForm
                                onChange={(bracketStyle: DIVISION_STYLE) =>
                                    onChange({
                                        ...division,
                                        bracketStyle,
                                        classes:
                                            // reset classes if switching to Madison System
                                            bracketStyle === DIVISION_STYLE.FIXED_WEIGHT_CLASSES
                                                ? [GET_NULL_CLASS(0)]
                                                : [],
                                    })
                                }
                                value={bracketStyle}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 1 }}>{removeButton}</Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <DivisionLimitForm
                                onChange={(limit: DIVISION_LIMITATION) => {
                                    onChange({
                                        ...division,
                                        entryLimitation: limit,
                                    });
                                    trigger('divisions');
                                }}
                                value={entryLimitation}
                            />
                        </Grid2>
                        {limitationSelected ? (
                            <>
                                <Grid2 size={{ xs: 6, sm: 3 }}>
                                    <FormInputNumber
                                        label={`Min ${entryLimitation === DIVISION_LIMITATION.AGE ? 'Age' : 'Grade'}`}
                                        name={`divisions[${index}].minAge`}
                                        numberInputProps={{ placeholder: 'No Minimum' }}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 3 }}>
                                    <FormInputNumber
                                        label={`Max ${entryLimitation === DIVISION_LIMITATION.AGE ? 'Age' : 'Grade'}`}
                                        name={`divisions[${index}].maxAge`}
                                        numberInputProps={{ placeholder: 'No Limit' }}
                                    />
                                </Grid2>
                            </>
                        ) : null}
                    </Grid2>

                    {bracketStyle === DIVISION_STYLE.MADISON_SYSTEM ? (
                        <Alert severity="info" sx={{ marginBottom: 1 }}>
                            <AlertTitle>Madison System</AlertTitle>
                            Madison System divisions do not have weight classes. Wrestlers will be
                            grouped into brackets based on their weight and skill level.
                        </Alert>
                    ) : (
                        <ClassListEditor
                            classes={classes}
                            isDivisionRemoved={isRemoved}
                            divisionIndex={index}
                            onChange={(classes) => {
                                onChange({ ...division, classes });
                            }}
                        />
                    )}
                </Box>
            )}
        </Draggable>
    );
}
function ClassListEditor({
    classes,
    isDivisionRemoved,
    divisionIndex,
    onChange,
}: {
    classes: ClassToInsert[];
    isDivisionRemoved: boolean;
    divisionIndex: number;
    onChange: (classes: ClassToInsert[]) => void;
}) {
    const addClass = useCallback(() => {
        classes.push(GET_NULL_CLASS(classes.length));
        onChange(classes);
    }, [classes, onChange]);

    // only use this for classes that have not been saved to the DB yet
    // if they're in the DB we'll just mark them as removed
    const removeClass_unsafe = (index: number) => {
        console.log('remove class', index);
        classes.splice(index, 1);
        onChange(classes);
    };

    return (
        <>
            <Box
                key={`${divisionIndex}-classes`}
                display="flex"
                marginLeft="2rem"
                flexDirection="column"
                borderRadius={2}
                padding={2}
                sx={{
                    border: 1,
                    // backgroundColor: 'rgba(191, 191, 191, .05)',
                    // background: 'linear-gradient(to bottom, transparent, #f9f9f9 )',
                    background: 'radial-gradient(transparent,transparent, #f1f1f1)',
                    borderColor: 'rgba(191, 191, 191, .5)',
                }}
            >
                <Typography>
                    Weight Classes
                    <IconButton
                        color="primary"
                        aria-label="add class"
                        onClick={addClass}
                        sx={{ marginLeft: 1, marginTop: -0.5 }}
                        tabIndex={(divisionIndex + 1) * 10000}
                    >
                        <AddCircleOutline />
                    </IconButton>
                </Typography>
                <Droppable
                    droppableId={getDraggableIdFromIndex(divisionIndex)}
                    type={'weight-class'}
                    key={`weight-class-${divisionIndex}`}
                >
                    {(dropProvided, dropSnapshot) => (
                        <Box
                            sx={{
                                background: dropSnapshot.isDraggingOver
                                    ? 'rgba(0, 168, 34, .3)'
                                    : 'transparent',
                                transition: 'background 0.1s ease',
                                paddingBottom: dropSnapshot.isDraggingOver ? 6 : 0, // pad bottom to make room for the drop indicator
                                // Focus styles
                                ':focus': {
                                    outline: '2px solid blue',
                                    outlineOffset: '2px',
                                },
                            }}
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                        >
                            {classes
                                ?.sort((a, b) => a.sortIndex - b.sortIndex)
                                .map((classValue /* class is reserved word */, index) => (
                                    <Draggable
                                        draggableId={`${divisionIndex}-class-${index}`}
                                        key={`${divisionIndex}-class-${index}`}
                                        index={index}
                                    >
                                        {(dragProvided, snapshot) => (
                                            <Box
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                {...dragProvided.dragHandleProps}
                                                sx={{
                                                    background: snapshot.isDragging
                                                        ? 'white'
                                                        : undefined,
                                                    transition: 'background 0.1s ease',
                                                }}
                                            >
                                                <ClassEditor
                                                    // key={`${divisionIndex}-class-${index}`}
                                                    addClass={addClass}
                                                    autoFocus={
                                                        index !== 0 &&
                                                        !classValue.name &&
                                                        index === classes.length - 1
                                                    }
                                                    classValue={classValue}
                                                    divisionIndex={divisionIndex}
                                                    index={index}
                                                    isDivisionRemoved={isDivisionRemoved}
                                                    onChange={(updatedClass) => {
                                                        const newClasses = [...classes];
                                                        newClasses[index] = updatedClass;
                                                        onChange(newClasses);
                                                    }}
                                                    removeClass_unsafe={removeClass_unsafe}
                                                />
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                            {/* Placeholder for dragging */}
                            {dropProvided.placeholder}
                        </Box>
                    )}
                </Droppable>

                <Box sx={{ paddingTop: 1 }}>
                    <Button
                        startIcon={<AddCircleOutline />}
                        variant="outlined"
                        onClick={addClass}
                        // sx={{ flexShrink: 0.3 }}
                    >
                        Add class
                    </Button>
                </Box>
            </Box>
            <Divider sx={{ marginY: 1 }} />
        </>
    );
}

function ClassEditor({
    addClass,
    autoFocus,
    classValue,
    divisionIndex,
    index,
    isDivisionRemoved,
    onChange,
    removeClass_unsafe,
}: {
    addClass: () => void;
    autoFocus?: boolean;
    classValue: ClassToInsert;
    divisionIndex: number;
    index: number;
    isDivisionRemoved: boolean;
    onChange: (classValue: ClassToInsert) => void;
    removeClass_unsafe: (index: number) => void;
}) {
    const { trigger, watch } = useFormContext<EventForm>();
    const [weightTolerance] = watch(['weightTolerance']);
    const inputRef = useRef<HTMLInputElement>();

    // We keep "removed" items around so we can mark them accordingly in the DB...
    const isRemoved = useMemo(() => classValue?.status === STATUS.REMOVED, [classValue?.status]);

    const { name, maxWeight } = classValue;

    // focus input after render
    useEffect(() => {
        if (autoFocus) {
            const timeout = setTimeout(() => inputRef.current?.focus(), 30);
            return () => clearTimeout(timeout);
        }
    }, [autoFocus, index]);

    if (isRemoved || isDivisionRemoved) return null;

    return (
        <Grid2 container alignItems={'center'} spacing={2} paddingTop={1}>
            <Grid2 size={{ xs: 12, sm: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <DragIndicator sx={{ paddingRight: 0.25 }} />
                    <TextField
                        fullWidth
                        inputRef={inputRef}
                        label={`Weight Class Name #${index + 1}`}
                        onChange={({ target: { value } }) => {
                            // if the user names the class a number, we'll assume it's also meant to be the max weight
                            const isAllNumbers =
                                value.replace(/[^0-9.]/g, '').length === value.length &&
                                maxWeight?.replace(/[^0-9.]/g, '').length === maxWeight?.length;

                            onChange({
                                ...classValue,
                                // if the user names the class a number, we'll assume it's also meant to be the max weight
                                maxWeight: isAllNumbers
                                    ? // Automatically add the weight tolerance to the max weight
                                      (
                                          Number(value.replace(/[^0-9.]/g, '')) +
                                          (weightTolerance || 0)
                                      ).toString()
                                    : maxWeight,
                                name: value,
                            });
                            trigger('divisions');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                // If the user presses enter, we'll add a new class and focus the new input
                                addClass();
                            }
                        }}
                        required
                        size="small"
                        value={name}
                        variant="outlined"
                        // helperText={error ? error.message : null}
                    />
                </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
                {/* use TextField instead of FormInputText because when other fields modify this one it doesn't re-render with FormInputText */}
                {/* max weight needs to be a string to accommodate units like "Kg" or "Lbs" */}
                <TextField
                    fullWidth
                    label="Max Weight"
                    onChange={({ target: { value } }) => {
                        // max weight needs to be a string to accommodate units like "Kg" or "Lbs"
                        onChange({ ...classValue, maxWeight: value });
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            // If the user presses enter, we'll add a new class and focus the new input
                            addClass();
                        }
                    }}
                    placeholder="No Limit"
                    size="small"
                    slotProps={{
                        input: {
                            endAdornment:
                                // show lbs scale if there is no maxWeight yet
                                !maxWeight ||
                                // if maxWeight is a number, we'll show the default lbs scale
                                // Hide the scale if the user has entered a custom unit
                                // (i.e. '12kg' or '12 Lbs' or '12 Lbs.' or 'Heavyweight')
                                maxWeight.replace(/[\d.]/g, '').length === 0 ? (
                                    <InputAdornment position="end">
                                        lbs
                                        <Scale sx={{ marginLeft: 1 }} />
                                    </InputAdornment>
                                ) : null,
                            // tab from weight name to weight input
                            tabIndex: (divisionIndex + 1) * 100 + index + 2,
                        },
                    }}
                    value={maxWeight}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 3 }}>
                <FormInputNumber
                    label="Max Entries (optional)"
                    name={`divisions[${divisionIndex}].classes[${index}].maxEntries`}
                    numberInputProps={{
                        onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                // If the user presses enter, we'll add a new class and focus the new input
                                addClass();
                            }
                        },
                        placeholder: 'No limit',
                        slotProps: {
                            // tab from weight name to weight input
                            input: { tabIndex: (divisionIndex + 1) * 100 + index + 2 },
                        },
                    }}
                />
            </Grid2>

            <Grid2 size={{ xs: 2, sm: 1 }}>
                <IconButton
                    tabIndex={(divisionIndex + 1) * 10000 + index * 10}
                    type="reset"
                    color="error"
                    // className="dynamic-delete-button"
                    onClick={() => {
                        // FIXME: test this logic
                        if ('id' in classValue && classValue.id) {
                            // if the division already exists in our DB, we need to mark it as deleted, not remove it from the form
                            Object.assign(classValue, {
                                status: STATUS.REMOVED,
                            });
                        } else {
                            removeClass_unsafe(index);
                        }
                    }}
                >
                    <RemoveCircleOutline />
                </IconButton>
            </Grid2>
        </Grid2>
    );
}
